/** @jsx isolateComponent */
import { useState } from 'react';
import { css } from '@emotion/react';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { debounce } from '../../../utils/debounce';
import { Colors, Typography } from '../../../theme';
import { Vars } from './Vars';

type Props = {
  /**
   * The content to render within the component.
   * Typically `TableHead`, `TableBody`, and `TableFoot` components.
   */
  children: React.ReactNode;
  /**
   * Whether to collapse the borders of the table cells.
   */
  collapse?: boolean;
  /**
   * The kind of table to render. Controls its visual styles.
   * When a non-`custom` `kind` is supplied, `Table` will style
   * itself and its subcomponents (e.g. `td`, `th`, `tr` nodes).
   * When a `custom` `kind` is supplied, consumers are responsible
   * for almost all styling.
   */
  kind?: 'standard' | 'custom';
  /**
   * Whether `Table` can be scrolled.
   */
  scrollable?: boolean;
};

const Styles = labelStyles('Table', {
  root: css`
    display: block;
    width: 100%;
    position: relative;

    &[data-scrollable] {
      overflow: auto;

      &[data-scrolled-horz] {
        ${Vars.frozenCellHorzShadowOpacity}: 1;
      }

      &[data-scrolled-vert] {
        ${Vars.frozenCellVertShadowOpacity}: 1;
      }
    }
  `,
  table: css`
    display: table;

    &[data-collapse] {
      border-collapse: collapse;
    }

    &[data-kind='standard'] {
      width: 100%;
      ${Typography.paragraph3};
      color: ${Colors.neutral800};

      th,
      td {
        border-bottom: 1px solid ${Colors.neutral400};
        background-color: ${Colors.neutral100};
        padding: 15px 10px;
      }

      tbody tr:hover {
        th,
        td {
          background-color: ${Colors.blue10};
        }
      }
    }
  `,
});

const defaultProps = {
  collapse: false,
  kind: 'standard',
};

/**
 * Highly flexible component for displaying data.
 */
export const Table = (_props: Props) => {
  const props = {
    ...defaultProps,
    ..._props,
  };
  const [isScrolledHorz, setIsScrolledHorz] = useState(false);
  const [isScrolledVert, setIsScrolledVert] = useState(false);
  // Debounce to prevent state thrashing. Separate debounced
  // function from `onScroll` to work around event pooling
  // https://reactjs.org/docs/legacy-event-pooling.html
  const setScroll = debounce((target: HTMLDivElement) => {
    const { scrollLeft, scrollTop } = target;
    setIsScrolledHorz(scrollLeft > 0);
    setIsScrolledVert(scrollTop > 0);
  }, 20);
  const onScroll = (e: React.SyntheticEvent) => {
    setScroll(e.target);
  };
  return (
    <div
      css={Styles.root}
      data-scrollable={props.scrollable ? '' : undefined}
      data-scrolled-horz={isScrolledHorz ? '' : undefined}
      data-scrolled-vert={isScrolledVert ? '' : undefined}
      onScroll={onScroll}
    >
      <table
        css={Styles.table}
        data-collapse={props.collapse ? '' : undefined}
        data-kind={props.kind}
        role="table"
      >
        {props.children}
      </table>
    </div>
  );
};
